import { useSearchParams } from 'react-router-dom';
import { FilterParamKeys } from '@gokrew/models/constants';
import { useMemo } from 'react';
import { useDebounce } from './useDebounce';

/* -------------------------------------------------------------------------- */
/*                                Helper Utils                                */
/* -------------------------------------------------------------------------- */

/**
 * Checks if value is a plain object (not array/null)
 */
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Recursively appends params using bracket notation.
 *
 * Examples:
 *   { a: 1 } → a=1
 *   { tags: ['a'] } → tags[]=a
 *   { filters: { status: 'active' } } → filters[status]=active
 *
 * Also includes circular reference protection via WeakSet.
 */
function appendParam(
  params: URLSearchParams,
  key: string,
  value: unknown,
  seen: WeakSet<object>
) {
  // Skip empty values (keeps URL clean)
  if (value === '' || value === null || value === undefined) return;

  // 🔧 FIX #1 — track per-branch to avoid diamond false positives
  let trackedValue: object | null = null;

  if (typeof value === 'object' && value !== null) {
    if (seen.has(value)) {
      throw new Error('Circular structure in query params');
    }
    seen.add(value);
    trackedValue = value;
  }

  // Arrays → key[]
  if (Array.isArray(value)) {
    // ✅ Top-level primitive array → comma format
    const isTopLevel = !key.includes('[');
    const isPrimitiveArray = value.every(
      (v) => typeof v !== 'object' || v === null
    );

    if (isTopLevel && isPrimitiveArray) {
      params.append(key, value.join(','));
      if (trackedValue) seen.delete(trackedValue);
      return;
    }

    // 🔹 Nested arrays → bracket notation
    value.forEach((v) => appendParam(params, `${key}[]`, v, seen));

    if (trackedValue) seen.delete(trackedValue);
    return;
  }

  // Nested objects → key[subKey]
  if (isPlainObject(value)) {
    Object.entries(value).forEach(([k, v]) => {
      appendParam(params, `${key}[${k}]`, v, seen);
    });

    if (trackedValue) seen.delete(trackedValue);
    return;
  }

  // Booleans must be stringified explicitly
  if (typeof value === 'boolean') {
    params.append(key, value ? 'true' : 'false');
    if (trackedValue) seen.delete(trackedValue);
    return;
  }

  // Primitives
  params.append(key, String(value));

  if (trackedValue) seen.delete(trackedValue);
}

/**
 * Deep setter used while reconstructing nested objects
 * from bracket notation keys.
 */
function setDeep(
  obj: Record<string, unknown>,
  path: string[],
  value: string
): void {
  let current: unknown = obj;

  path.forEach((segment, index) => {
    const isLast = index === path.length - 1;

    if (segment === '') {
      if (!Array.isArray(current)) {
        throw new Error('Invalid array structure in query params');
      }

      if (isLast) {
        (current as unknown[]).push(value);
      } else {
        const next: Record<string, unknown> = {};
        (current as unknown[]).push(next);
        current = next;
      }
      return;
    }

    if (isLast) {
      if (
        typeof current === 'object' &&
        current !== null &&
        segment in (current as Record<string, unknown>)
      ) {
        const existing = (current as Record<string, unknown>)[segment];
        if (Array.isArray(existing)) {
          (existing as unknown[]).push(value);
        } else {
          (current as Record<string, unknown>)[segment] = [existing, value];
        }
      } else {
        if (typeof current === 'object' && current !== null) {
          (current as Record<string, unknown>)[segment] = value;
        } else {
          throw new Error('Invalid structure in query params');
        }
      }
    } else {
      if (typeof current !== 'object' || current === null) {
        throw new Error('Invalid structure in query params');
      }
      const nextIsArray = path[index + 1] === '';
      const currRecord = current as Record<string, unknown>;
      if (!currRecord[segment] || typeof currRecord[segment] !== 'object') {
        currRecord[segment] = nextIsArray ? [] : {};
      }
      current = currRecord[segment];
    }
  });
}

function deepMerge(
  base: Record<string, unknown>,
  override: Record<string, unknown>
) {
  const result = { ...base };
  for (const key of Object.keys(override)) {
    if (isPlainObject(base[key]) && isPlainObject(override[key])) {
      result[key] = deepMerge(
        base[key] as Record<string, unknown>,
        override[key] as Record<string, unknown>
      );
    } else {
      result[key] = override[key];
    }
  }
  return result;
}

/**
 * Parses entire URLSearchParams into nested object
 * supporting bracket notation.
 */
function parseAll(searchParams: URLSearchParams): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  searchParams.forEach((value, rawKey) => {
    if (!rawKey.includes('[')) {
      if (result[rawKey] !== undefined) {
        const existing = result[rawKey];
        if (Array.isArray(existing)) {
          (existing as string[]).push(value);
        } else {
          result[rawKey] = [existing as string, value];
        }
      } else {
        result[rawKey] = value;
      }
      return;
    }

    const path = rawKey.replace(/\]/g, '').split('[');
    setDeep(result, path, value);
  });

  return result;
}
/* -------------------------------------------------------------------------- */
/*                                   Hook                                     */
/* -------------------------------------------------------------------------- */

/**
 * Hook that syncs URL query params with typed defaults.
 *
 * ✅ Supports primitives (string/number/boolean)
 * ✅ Supports nested objects via bracket notation
 * ✅ Supports nested arrays
 * ✅ Includes circular reference protection
 * ✅ Preserves existing PAGE reset behavior
 *
 * NOTE:
 * - Arrays of objects are not perfectly reconstructable (by design)
 * - Defaults shape should match expected query structure
 */
export function useQueryParams<T extends Record<string, unknown>>(defaults: T) {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Build typed params object by merging:
   *   URL params → defaults → type casting
   */
  const params = useMemo(() => {
    const parsed = parseAll(searchParams);

    const mergedObj = deepMerge({ ...defaults }, parsed);

    // Apply type casting based on defaults
    Object.keys(defaults).forEach((key) => {
      const defaultValue = defaults[key as keyof T];
      const value = mergedObj[key];

      if (value === undefined) return;

      if (Array.isArray(defaultValue)) {
        if (typeof value === 'string') {
          mergedObj[key] = value ? (value.split(',') as string[]) : [];
        }
        return;
      }

      if (typeof defaultValue === 'number') {
        const num = Number(value as unknown);
        mergedObj[key] = Number.isNaN(num) ? defaultValue : num;
      } else if (typeof defaultValue === 'boolean') {
        mergedObj[key] =
          typeof value === 'boolean'
            ? value
            : (value as string) === 'true' || (value as string) === '1';
      }
    });

    return mergedObj as T;
  }, [searchParams, defaults]);

  /**
   * Updates the URL search params.
   *
   * Behavior:
   * - Merges with existing params
   * - Serializes using bracket notation
   * - Resets PAGE to '1' when other filters change
   */
  const updateParams = (updates: Partial<T>, resetPage = true) => {
    const newParams = new URLSearchParams();

    // Start from current parsed state to preserve untouched keys
    const current = parseAll(searchParams);
    const merged = deepMerge(current, updates as Record<string, unknown>);

    // WeakSet used to detect circular references
    Object.entries(merged).forEach(([key, value]) => {
      appendParam(newParams, key, value, new WeakSet());
    });

    // Auto-reset page when filters change
    if (
      resetPage &&
      !(FilterParamKeys.PAGE in updates) &&
      newParams.has(FilterParamKeys.PAGE)
    ) {
      newParams.set(FilterParamKeys.PAGE, '1');
    }

    setSearchParams(newParams);
  };

  return { params, updateParams };
}

/**
 * Deserializes an array of objects that was round-tripped through useQueryParams.
 *
 * useQueryParams serializes [{a:'1', b:'2'}] as separate URL entries
 * (key[][a]=1 & key[][b]=2), then deserializes them as [{a:'1'}, {b:'2'}] —
 * one partial object per key. This function merges those partials back into
 * complete objects by accumulating entries until all requiredKeys are present.
 *
 * @example
 * const designations = deserializeObjectArray(params.designation, ['label', 'value']);
 * // [{label: 'PM', value: 'uuid'}]
 */
export type SerializedFilterOption = { label: string; value: string };

/**
 * Combines `deserializeObjectArray` and `useDebounce` for `{ label, value }[]`
 * filter params that are round-tripped through the URL.
 *
 * @example
 * const { items: designations, debouncedIds } = useSerializedFilter(params.designation);
 */
export function useSerializedFilter(raw: unknown, debounceMs = 400) {
  const items = useMemo(
    () =>
      deserializeObjectArray<SerializedFilterOption>(raw, ['label', 'value']),
    [raw]
  );
  const ids = useMemo(() => items.map((i) => i.value), [items]);
  const debouncedIds = useDebounce(ids, debounceMs);
  return { items, debouncedIds };
}

export function deserializeObjectArray<T extends Record<string, unknown>>(
  raw: unknown,
  requiredKeys: (keyof T)[]
): T[] {
  const items: Partial<T>[] = Array.isArray(raw)
    ? (raw as Partial<T>[])
    : raw !== null && typeof raw === 'object'
      ? Object.values(raw as Record<string, Partial<T>>)
      : [];

  const result: T[] = [];
  let current: Partial<T> = {};

  for (const item of items) {
    Object.assign(current, item);
    if (requiredKeys.every((k) => current[k] !== undefined)) {
      result.push({ ...current } as T);
      current = {};
    }
  }

  return result;
}
